import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { bracketLinkHoverStyle } from "../../styles/linkStyles.js";

// --- Animations ---
const blink = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
`;

// Chevron hover animations
const chevronSpin = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
`;

const chevronShear = keyframes`
    0% { transform: skewX(0deg); }
    25% { transform: skewX(35deg); }
    50% { transform: skewX(-35deg); }
    75% { transform: skewX(35deg); }
    100% { transform: skewX(0deg); }
`;

const chevronWiggle = keyframes`
    0%, 100% { transform: translateX(0); }
    20% { transform: translateX(-6px); }
    40% { transform: translateX(6px); }
    60% { transform: translateX(-4px); }
    80% { transform: translateX(4px); }
`;

const chevronNod = keyframes`
    0%, 100% { transform: rotate(0deg); }
    30% { transform: rotate(35deg); }
    60% { transform: rotate(-15deg); }
`;

const chevronFlipY = keyframes`
    0% { transform: rotateY(0deg); }
    100% { transform: rotateY(1080deg); }
`;

const chevronBounce = keyframes`
    0%, 100% { transform: translateY(0); }
    20% { transform: translateY(-12px); }
    40% { transform: translateY(0); }
    55% { transform: translateY(-6px); }
    70% { transform: translateY(0); }
    85% { transform: translateY(-3px); }
`;

const chevronFlipX = keyframes`
    0% { transform: rotateX(0deg); }
    100% { transform: rotateX(360deg); }
`;

const chevronRubber = keyframes`
    0%, 100% { transform: scaleX(1) scaleY(1); }
    30% { transform: scaleX(1.4) scaleY(0.75); }
    50% { transform: scaleX(0.85) scaleY(1.15); }
    70% { transform: scaleX(1.15) scaleY(0.9); }
`;

const chevronBoing = keyframes`
    0% { transform: scale(1); }
    30% { transform: scale(1.5); }
    50% { transform: scale(0.9); }
    70% { transform: scale(1.15); }
    85% { transform: scale(0.97); }
    100% { transform: scale(1); }
`;

// Array of animation names for random selection
const CHEVRON_ANIMATIONS = [
    { name: 'spin', keyframes: chevronSpin, duration: '0.5s' },
    { name: 'shear', keyframes: chevronShear, duration: '0.8s' },
    { name: 'wiggle', keyframes: chevronWiggle, duration: '0.3s' },
    { name: 'nod', keyframes: chevronNod, duration: '0.8s' },
    { name: 'flipY', keyframes: chevronFlipY, duration: '1.2s' },
    { name: 'bounce', keyframes: chevronBounce, duration: '0.6s' },
    { name: 'flipX', keyframes: chevronFlipX, duration: '0.5s' },
    { name: 'rubber', keyframes: chevronRubber, duration: '0.5s' },
    { name: 'boing', keyframes: chevronBoing, duration: '0.5s' },
];

// --- Styled components ---
const HeaderWrapper = styled.header`
    padding: 30px 0;
    position: relative;
    width: 100vw;
    z-index: 10;
`;

const HeaderContent = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    box-sizing: border-box;
`;

const Logo = styled(Link)`
    font-size: 24px;
    font-weight: 700;
    color: var(--text-color);
    text-decoration: none;
    text-transform: uppercase;
    letter-spacing: 2px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
`;

// Chevron as a real element for animation control
const Chevron = styled.span`
    color: var(--primary-color);
    margin-right: 10px;
    font-weight: 800;
    font-size: 1.3em;
    display: inline-block;
    animation: ${props => props.$animation
        ? css`${props.$animation.keyframes} ${props.$animation.duration} ease-in-out`
        : css`${blink} 1s infinite`
    };

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

// NavLinks now uses $isOpen instead of isOpen
const NavLinks = styled.nav`
    @media (max-width: 768px) {
        position: fixed;
        top: 0;
        right: ${props => (props.$isOpen ? '0' : '-100%')};
        width: 70%;
        height: 100vh;
        background-color: var(--background-color);
        z-index: 100;
        transition: right 0.3s ease;
        padding: 80px 30px 30px;
        box-shadow: -5px 0 15px rgba(0, 0, 0, 0.5);
        display: flex;
        flex-direction: column;
    }
    @media (max-width: 480px) {
        width: 80%;
    }

    @media (max-width: 320px) {
        width: 90%; /* Use more screen space on tiny devices */
        padding: 70px 20px 20px; /* Adjust padding */
    }
`;

const NavList = styled.ul`
    display: flex;
    list-style: none;
    padding: 0;
    margin: 0;

    @media (max-width: 768px) {
        flex-direction: column;
        width: 100%;
    }
`;

const NavItem = styled.li`
    margin-left: 40px;
    position: relative;

    &:first-child {
        margin-left: 0;
    }

    @media (max-width: 768px) {
        margin: 15px 0;
        width: 100%;
    }
`;

const NavLink = styled(Link)`
    color: var(--text-color);
    text-decoration: none;
    text-transform: uppercase;
    font-size: 14px;
    letter-spacing: 1px;
    display: block;
    padding: 5px;
    ${bracketLinkHoverStyle};

    @media (max-width: 768px) {
        padding: 10px 0;
        text-align: left;
        &:before, &:after {
            display: none;
        }
    }
`;

const MobileMenuButton = styled.button`
    display: none;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: var(--text-color);
    z-index: 101;
    visibility: ${props => props.$hidden ? 'hidden' : 'visible'};

    @media (max-width: 768px) {
        display: block;
    }

    @media (max-width: 320px) {
        padding: 8px; /* Slightly smaller touch target but still usable */
        font-size: 20px;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 20px;
    right: 20px;
    background: none;
    border: none;
    font-size: 30px;
    cursor: pointer;
    color: var(--primary-color);
    z-index: 105;
    display: none; // Hide by default

    @media (max-width: 768px) {
        display: ${props => props.$isMenuOpen ? 'block' : 'none'}; // Only show in mobile when menu is open
    }
`;

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [chevronAnimation, setChevronAnimation] = useState(null);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

    // Check for reduced motion preference
    React.useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        setPrefersReducedMotion(mediaQuery.matches);

        const handler = (e) => setPrefersReducedMotion(e.matches);
        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, []);

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
    const closeMenu = () => setIsMenuOpen(false);

    // Pick a random animation on hover (disabled if reduced motion)
    const handleLogoHover = useCallback(() => {
        if (prefersReducedMotion) return;
        const randomIndex = Math.floor(Math.random() * CHEVRON_ANIMATIONS.length);
        setChevronAnimation(CHEVRON_ANIMATIONS[randomIndex]);
    }, [prefersReducedMotion]);

    // Return to normal blink after animation ends
    const handleAnimationEnd = useCallback(() => {
        setChevronAnimation(null);
    }, []);

    return (
        <HeaderWrapper>
            <HeaderContent>
                <Logo to="/" onMouseEnter={handleLogoHover}>
                    <Chevron
                        $animation={chevronAnimation}
                        onAnimationEnd={handleAnimationEnd}
                    >
                        &gt;
                    </Chevron>
                    LPERRON_DEV
                </Logo>

                {/* Hide burger button when menu is open, but keep in DOM to prevent layout shift */}
                <MobileMenuButton
                    onClick={toggleMenu}
                    aria-label="Open menu"
                    aria-expanded={isMenuOpen}
                    aria-controls="mobile-navigation"
                    $hidden={isMenuOpen}
                >
                    ☰
                </MobileMenuButton>

                {/* Using $isOpen prop to properly pass to styled component */}
                <NavLinks id="mobile-navigation" $isOpen={isMenuOpen}>
                    <CloseButton
                        $isMenuOpen={isMenuOpen}
                        onClick={closeMenu}
                        aria-label="Close menu"
                    >
                        ×
                    </CloseButton>
                    <NavList>
                        <NavItem><NavLink to="/" onClick={closeMenu}>HOME</NavLink></NavItem>
                        <NavItem><NavLink to="/projects" onClick={closeMenu}>PROJECTS</NavLink></NavItem>
                        <NavItem><NavLink to="/about" onClick={closeMenu}>ABOUT</NavLink></NavItem>
                        <NavItem><NavLink to="/contact" onClick={closeMenu}>CONTACT</NavLink></NavItem>
                    </NavList>
                </NavLinks>
            </HeaderContent>
        </HeaderWrapper>
    );
};

export default Header;